import { Entity } from '@common/types/Entity';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { useFilePathPsetsData } from '@pages/Transpiler/useFilePathPsetsData';
import { theme } from '@prophecy/theme';
import { ColumnsType, Dialog, ExcelTable, Stack, Text } from '@prophecy/ui-v3';
import { clone } from '@prophecy/utils/data';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useImmer } from 'use-immer';

import { JobEntityType, PipelineEntityType, RecommendedPsetType } from '../../../../data/apis/transpileTypes';
import { StyledNameContainer } from '../../ImportedInfo/util';
import { FileTypes } from '../steps/types';
import { getBaseEntityIcon, searchAndFilterWithExtension } from '../steps/utils';
import { useImportState } from '../useImportState';

const StyledDialog = styled(Dialog)`
  width: 50%;
`;

export const RecommendedPsets = () => {
  const {
    transpilerState: {
      transpilerEntityTypes: { pipelines, jobs }
    },
    formDataWithActions: { recommendedPsetPath, setRecommendedPath },
    actions: { updateTranspiledPipeline, updateTranspilerJob }
  } = useImportState();

  const { pipelinePsets } = useFilePathPsetsData(recommendedPsetPath, false, setRecommendedPath);

  const recommendedPaths = useMemo(
    () =>
      searchAndFilterWithExtension(pipelinePsets, '', [FileTypes.pset]).filteredPaths.map((filePath) => ({
        filePath,
        enabled: true
      })),
    [pipelinePsets]
  );

  const [hasDialog, toggle] = useState(false);

  const [tableData, setTableData] = useImmer(recommendedPaths);

  useEffect(() => {
    setTableData(recommendedPaths);
    if (recommendedPsetPath && recommendedPaths.length > 0) {
      toggle(true);
    }
  }, [recommendedPaths, recommendedPsetPath, setTableData]);

  const [selectedRows, setSelectedRows] = useState<Array<string>>([]);

  let paths = recommendedPsetPath?.split('.');
  const isJob = paths.length > 0 && paths[paths.length - 1] === FileTypes.plan;

  const onSubmit = () => {
    const currentEntity = isJob
      ? clone(jobs)
          .reverse()
          .find((job) => {
            const path = job.filePath;
            return `/${path}` === recommendedPsetPath || path === recommendedPsetPath;
          })
      : clone(pipelines)
          .reverse()
          .find((pipeline) => {
            const path = pipeline.filePath;
            return `/${path}` === recommendedPsetPath || path === recommendedPsetPath;
          });

    const entityIndex = currentEntity?.__pathIndex as number;
    const transpilerOptions = currentEntity?.transpilerOptions || { pathsToBeReplaced: [], targetPsets: [] };
    const targetPsets = clone(transpilerOptions.targetPsets || []);
    tableData.forEach((row) => {
      if (selectedRows.includes(row.filePath)) {
        targetPsets.push(row.filePath);
      }
    });
    if (isJob) {
      updateTranspilerJob(
        {
          ...currentEntity,
          transpilerOptions: { ...transpilerOptions, targetPsets }
        } as JobEntityType,
        entityIndex
      );
    } else {
      updateTranspiledPipeline(entityIndex, {
        ...currentEntity,
        transpilerOptions: { ...transpilerOptions, targetPsets }
      } as PipelineEntityType);
    }
    setRecommendedPath('');
  };

  const datasetColumns: ColumnsType<RecommendedPsetType> = [
    {
      key: 'filePath',
      dataIndex: 'filePath',
      title: 'Parameter Set File Path',
      sortable: true,
      render: renderColumnWithEllipsis
    }
  ];

  if (recommendedPaths.length === 0) {
    return null;
  }
  const entity = isJob ? Entity.Job : Entity.Pipeline;
  const { icon, color } = getBaseEntityIcon(entity, 'xs');
  return (
    <StyledDialog
      data-test-id='recommended-psets-dialog'
      onOk={onSubmit}
      okButton={{ children: 'Add Parameter Set', variant: 'primary', disabled: selectedRows.length === 0 }}
      size='l'
      open={hasDialog}
      closable={true}
      closeButton={{ children: 'Skip' }}
      onClose={() => setRecommendedPath('')}
      subTitle={
        <Stack direction='horizontal' alignY='center' gap={theme.spaces.x10} width='100%' align='space-between'>
          {icon}
          <Text level='sm' tone={color}>
            {entity}
          </Text>
          <StyledNameContainer grow='1'>{renderColumnWithEllipsis(recommendedPsetPath)}</StyledNameContainer>
        </Stack>
      }
      title={
        <Text
          level='md'
          weight={theme.fontWeight.bold}
          hint={`Below is a list of recommended psets. Selected files will be added as default psets in your ${entity}.`}>
          Add Default Psets
        </Text>
      }>
      <Stack width='100%' height='300px' padding={`${theme.spaces.x16} 0`}>
        <ExcelTable
          rowSelection={{
            selectedRowKeys: selectedRows,
            onChange: (selectedArray) => setSelectedRows(selectedArray as string[])
          }}
          rowKey={'filePath'}
          fixedHeader={true}
          columns={datasetColumns}
          width='100%'
          data-test-id='recommended-psets-table'
          height='100%'
          placeholderRows={tableData.length < 9 ? 5 : 0}
          virtualize={true}
          dataSource={tableData}
        />
      </Stack>
    </StyledDialog>
  );
};
